<template>
  <div class="job-center">
    <!-- 篩選改成標籤（點一下即篩）：原本的下拉要先展開才知道有哪些值，標籤一眼看全 -->
    <div class="jc-bar">
      <div class="jc-filters">
        <div class="jc-filter-row">
          <span class="jc-filter-label">狀態</span>
          <span
            v-for="s in statusChips"
            :key="'s' + s.value"
            class="jc-chip"
            :class="{ on: statusFilter === s.value }"
            @click="pickStatus(s.value)"
          >
            <i v-if="s.dot" class="jc-dot" :class="'jc-dot-' + s.dot"></i>{{ s.label }}
          </span>
        </div>
        <div class="jc-filter-row">
          <span class="jc-filter-label">類型</span>
          <span
            v-for="t in typeChips"
            :key="'t' + t.value"
            class="jc-chip"
            :class="{ on: typeFilter === t.value }"
            @click="pickType(t.value)"
          >{{ t.label }}</span>
        </div>
      </div>
      <el-button type="primary" icon="el-icon-refresh" class="jc-refresh" @click="load(true)">
        重新整理
      </el-button>
    </div>

    <!-- 卡片式：原本那張表格（ID／任務號／範本／時間）資訊量不大卻占滿一屏，
         現在卡片上只留「產物預覽 + 狀態 + 兩個時間」，其餘一律進詳情看。
         往下滑到底自動載入下一頁（不再用點擊分頁）。
         捲軸留在**這裡**（ref="list"）—— 高度由 JS 算（彈窗 top=6vh、路由頁上方還有 header，
         寫死 calc(100vh - N) 一定對不上），這樣捲的是列表而不是整個彈窗。 -->
    <div ref="list" v-loading="loading && !list.length" class="jc-grid">
      <div v-for="row in list" :key="row.id" class="jc-card">
        <!-- 產物預覽區：有影片 → 點擊彈窗播放；只有圖片 → 疊放，點擊放大；沒產物 → 佔位 -->
        <div class="jc-preview">
          <div v-if="videoOf(row)" class="jc-video-box" @click="playVideo(row)">
            <video class="jc-video-thumb" :src="fileUrl(videoOf(row))" muted preload="metadata"></video>
            <i class="el-icon-video-play jc-video-play"></i>
          </div>

          <div v-else-if="imagesOf(row).length" class="jc-stack">
            <el-image
              v-for="(p, i) in imagesOf(row).slice(0, 3)"
              :key="p + '-' + i"
              class="jc-stack-img"
              :style="stackStyle(i)"
              :src="thumbUrl(p, 320)"
              :preview-src-list="imagesOf(row).map(fileUrl)"
              fit="contain"
            ></el-image>
            <span v-if="imagesOf(row).length > 3" class="jc-badge">+{{ imagesOf(row).length - 3 }}</span>
          </div>

          <div v-else class="jc-blank">
            <i :class="blankIcon(row)"></i>
            <span>{{ blankText(row) }}</span>
          </div>
        </div>

        <div class="jc-body">
          <div class="jc-head">
            <span class="jc-no">#{{ row.id }}</span>
            <span class="jc-type">{{ typeOf(row) }}</span>
            <!-- 狀態用圓點表示：綠=成功、紅=失敗，其餘用中性色 -->
            <span class="jc-status">
              <i class="jc-dot" :class="'jc-dot-' + dotOf(row.status)"></i>
              {{ statusLabel(row.status) }}
            </span>
          </div>
          <!-- 卡片是固定高度的，行數必須省著用：範本這種次要資訊（而且值是 video 這種內部名）
               不在卡片上顯示，進詳情看。 -->
          <div class="jc-line">建立：{{ shortTime(row.created_at) || '-' }}</div>
          <div class="jc-line">完成：{{ shortTime(row.finished_at) || '—' }}</div>
          <div class="jc-actions">
            <el-button size="mini" type="text" icon="el-icon-document" @click="openDetail(row)">
              詳情
            </el-button>
            <el-button
              v-if="canCancel(row.status)"
              size="mini"
              type="text"
              class="danger"
              icon="el-icon-close"
              @click="onCancel(row)"
            >取消</el-button>
          </div>
        </div>
      </div>

      <div v-if="!loading && !list.length" class="jc-none">還沒有任務</div>

      <!-- 只留一個最輕的「載入中」：原本那行「繼續下滑載入更多 / 沒有更多了」太醜，去掉了 -->
      <div v-if="loading" class="jc-loading"><i class="el-icon-loading"></i> 載入中</div>

      <!-- 哨兵：進入視口就載入下一頁。用 IntersectionObserver 而不是監聽某個容器的 scroll ——
           任務中心在彈窗裡和獨立頁裡是兩種捲動容器，寫死監聽哪一個都會漏。 -->
      <div ref="sentinel" class="jc-sentinel"></div>
    </div>

    <!-- 影片彈窗播放 -->
    <el-dialog
      :visible.sync="videoVisible"
      :title="videoTitle"
      width="760px"
      top="8vh"
      append-to-body
      destroy-on-close
      custom-class="jc-video-dialog"
    >
      <video v-if="videoVisible" :src="fileUrl(videoPath)" class="jc-video" controls autoplay></video>
    </el-dialog>

    <!-- 任務詳情：完整資訊（步驟、失敗原因、產物下載、畫質提升）都在這裡。
         關掉時只刷新「這一條」—— 詳情裡畫質提升會把原影片替換掉，卡片得跟著變；
         整頁重載會把捲動位置沖掉，使用者正翻到第 5 頁就被彈回頂部了。 -->
    <el-dialog
      :visible.sync="detailVisible"
      :title="'任務詳情 #' + (detailId || '')"
      width="760px"
      :modal="false"
      append-to-body
      destroy-on-close
      @close="refreshOne(detailId)"
    >
      <JobProgress v-if="detailVisible && detailId" :job-id="detailId" hide-progress />
    </el-dialog>
  </div>
</template>

<script>
import { fetchJobs, fetchJobDetail, cancelJob } from '../api/jobs'
import { fileUrl, thumbUrl } from '../utils/file'
import JobProgress from '../components/JobProgress.vue'

/** 狀態：下拉選項 + 卡片上的中文文案 / 圓點顏色（綠=成功、紅=失敗） */
const STATUSES = [
  { value: 'pending', label: '待處理' },
  { value: 'queued', label: '排隊中' },
  { value: 'running', label: '生成中' },
  { value: 'success', label: '已完成' },
  { value: 'failed', label: '失敗' },
  { value: 'retrying', label: '重試中' },
  { value: 'cancelled', label: '已取消' }
]
const STATUS_LABEL = {}
const STATUS_DOT = {}
STATUSES.forEach((s) => {
  STATUS_LABEL[s.value] = s.label
})
STATUS_DOT.success = 'ok'        // 綠
STATUS_DOT.failed = 'err'        // 紅
STATUS_DOT.running = 'run'       // 藍 + 呼吸
STATUS_DOT.retrying = 'run'
STATUS_DOT.queued = 'wait'       // 灰
STATUS_DOT.pending = 'wait'
STATUS_DOT.cancelled = 'wait'

/** 狀態篩選標籤（'' = 全部）：帶上對應圓點色，跟卡片上的狀態一眼對得上 */
const STATUS_CHIPS = [{ value: '', label: '全部' }].concat(
  STATUSES.map((s) => ({ value: s.value, label: s.label, dot: STATUS_DOT[s.value] }))
)

/**
 * 任務類型篩選標籤（'' = 全部）。
 * 用詞跟 constants/imageOptions.js 的 MATERIAL_TYPES 一致（電商主圖／電商詳情圖…），別另編一套。
 * 影片拆成「影片創作 / 影片復刻」兩項：兩者 kit_type 都是 'video'，
 * 後端按子任務 params 裡的 ref_video / prompt_clone 區分（傳 video / video_remake）。
 * 「競品參考」後端沒有對應的流程範本（抓競品圖是詳情圖流程裡的功能，不是任務類型），
 * 篩它會是空的 —— 但要求列全，所以留著。
 */
const TYPE_CHIPS = [
  { value: '', label: '全部' },
  { value: 'main_image', label: '電商主圖' },
  { value: 'detail_page', label: '電商詳情圖' },
  { value: 'sku_set', label: '電商SKU圖' },
  { value: 'promo_bundle', label: '電商推廣圖' },
  { value: 'scene_image', label: '場景圖' },
  { value: 'competitor_ref', label: '競品參考' },
  { value: 'video', label: '影片創作' },
  { value: 'video_remake', label: '影片復刻' }
]

/** kit_type（WorkflowTemplates 的範本 id）→ 中文任務類型 */
const KIT_LABEL = {
  main_image: '電商主圖',
  detail_page: '電商詳情圖',
  sku_set: '電商SKU圖',
  promo_bundle: '電商推廣圖',
  scene_image: '場景圖',
  competitor_ref: '競品參考',   // 後端目前沒有對應範本，留著備用
  full_kit: '全流程',
  video: '影片創作'             // 復刻也是 video，靠後端給的 remake 標記區分
}

const IMAGE_RE = /\.(png|jpe?g|webp|gif|bmp)$/i
const VIDEO_RE = /\.(mp4|webm|mov|m4v)$/i

/** 哨兵進入視口多大範圍就開預載入（配合 IntersectionObserver 的 rootMargin） */
const SENTINEL_MARGIN = 260

export default {
  name: 'JobCenter',
  components: { JobProgress },
  data() {
    return {
      statusChips: STATUS_CHIPS,
      typeChips: TYPE_CHIPS,
      list: [],
      total: 0,
      page: 0,              // 已載入到第幾頁（0 = 還沒載入）
      pageSize: 15,
      finished: false,      // 沒有更多了
      loadSeq: 0,           // 請求序號：舊請求的回應不許覆蓋新請求的結果
      observer: null,       // 下滑載入的哨兵觀察器
      resizeTimer: null,    // 彈窗動畫結束後重新量高度的計時器
      statusFilter: '',
      typeFilter: '',
      loading: false,
      detailVisible: false,
      detailId: null,
      videoVisible: false,
      videoPath: '',
      videoTitle: ''
    }
  },
  created() {
    this.load(true)
  },
  mounted() {
    this.observeSentinel()
    this.resize()
    // 彈窗是帶過渡動畫的：掛載瞬間量出來的 top 還偏上，動畫結束後（300ms）再量一次
    this.resizeTimer = setTimeout(this.resize, 360)
    window.addEventListener('resize', this.resize)
  },
  beforeDestroy() {
    clearTimeout(this.resizeTimer)
    window.removeEventListener('resize', this.resize)
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  },
  methods: {
    // ★模板裡要用的工具函式必須掛到 methods 上。
    // 只在 <script> 頂部 import 是不夠的：Vue 2 的模板只能存取元件實例上的屬性，
    // 否則渲染時報 `Property or method "fileUrl" is not defined`（已踩）。
    fileUrl,
    thumbUrl,

    /**
     * 載入任務列表。
     * @param {boolean} reset true = 從頭載入（首次 / 重新整理 / 換篩選）；false = 往列表末尾追加下一頁
     */
    async load(reset) {
      // 下滑會連續觸發，防並發重複請求；但手動重新整理（reset）允許插隊
      if (this.loading && !reset) return
      if (!reset && this.finished) return
      const seq = ++this.loadSeq
      this.loading = true
      const page = reset ? 1 : this.page + 1
      try {
        const data = await fetchJobs({
          page,
          pageSize: this.pageSize,
          status: this.statusFilter || undefined,
          type: this.typeFilter || undefined
        })
        if (seq !== this.loadSeq) return   // 已經有更新的請求了，丟棄這次結果
        const rows = data.list || []
        this.page = page
        this.total = data.totalRow || 0
        if (reset) {
          this.list = rows
        } else {
          // 新任務從頂部插入，會把老資料往下一頁擠 —— 追加時同一批可能重複，按 id 去重
          const seen = {}
          this.list.forEach((x) => {
            seen[x.id] = 1
          })
          rows.forEach((x) => {
            if (!seen[x.id]) {
              this.list.push(x)
            }
          })
        }
        this.finished = rows.length === 0 || this.list.length >= this.total
        if (reset) {
          this.toTop()
        }
      } catch (e) {
        if (seq !== this.loadSeq) return
        this.$message.error(e.message || '載入任務列表失敗')
      } finally {
        if (seq === this.loadSeq) {
          this.loading = false
          this.fillViewport()      // 一頁沒填滿螢幕就繼續拉（首屏自動補足）
        }
      }
    },

    /** 點狀態標籤（同一個再點一次不算改動，免得白刷一次） */
    pickStatus(v) {
      if (this.statusFilter === v) return
      this.statusFilter = v
      this.load(true)
    },

    /** 點類型標籤 */
    pickType(v) {
      if (this.typeFilter === v) return
      this.typeFilter = v
      this.load(true)
    },

    /** 回到列表頂部（捲軸在列表內部，直接設 scrollTop 最準） */
    toTop() {
      this.$nextTick(() => {
        if (this.$refs.list) {
          this.$refs.list.scrollTop = 0
        }
      })
    },

    /**
     * 給列表算一個可用高度 —— 捲軸留在列表內部。
     *
     * ★必須用 JS 量、不能寫死 calc(100vh - N)，而且**在彈窗裡要減掉彈窗自己的佔位**：
     * 任務中心是在頭像選單裡以 el-dialog 打開的（top = 6vh），彈窗下面還有
     * el-dialog__body 的 padding 和 el-dialog 自帶的 margin-bottom（約 50px）——
     * 只按視口算的話，列表底邊會把整個彈窗頂出視口，父容器就冒出垂直捲軸。
     */
    resize() {
      const el = this.$refs.list
      if (!el) return
      const listTop = el.getBoundingClientRect().top
      const dlg = el.closest ? el.closest('.el-dialog') : null
      let usable
      if (dlg) {
        const dlgTop = dlg.getBoundingClientRect().top
        const body = el.closest('.el-dialog__body')
        const padBottom = body
          ? parseFloat(window.getComputedStyle(body).paddingBottom) || 0
          : 0
        const marginBottom = parseFloat(window.getComputedStyle(dlg).marginBottom) || 0
        usable = window.innerHeight - dlgTop - (listTop - dlgTop) - padBottom - marginBottom - 8
      } else {
        usable = window.innerHeight - listTop - 16   // 獨立頁：到底就是視口底
      }
      el.style.maxHeight = Math.max(240, usable) + 'px'
    },

    /**
     * 用哨兵元素 + IntersectionObserver 觸發「往下滑載入下一頁」。
     *
     * ★別用 @scroll 監聽某個固定容器：任務中心有**兩種容器** ——
     * 頭像選單裡是 el-dialog（捲動發生在彈窗 wrapper 上），路由頁則是 .app-main 在捲。
     * 之前寫死監聽 .jc-grid 的 scroll，使用者在彈窗裡滑到底就完全沒反應（捲的其實是 wrapper）。
     * IntersectionObserver 只看「哨兵進沒進視口」，捲動發生在哪一層都不影響。
     */
    observeSentinel() {
      const el = this.$refs.sentinel
      if (!el || typeof IntersectionObserver === 'undefined') {
        return                      // 老瀏覽器：下滑載入不可用，手動點重新整理即可
      }
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.load(false)
        }
      }, { rootMargin: SENTINEL_MARGIN + 'px' })
      this.observer.observe(el)
    },

    /**
     * 一頁不夠一屏時繼續補。
     * 否則哨兵從頭到尾都在視口裡、沒有第二次「交叉」事件，後面幾頁就再也載入不出來。
     */
    fillViewport() {
      if (this.finished || this.loading) return
      this.$nextTick(() => {
        const el = this.$refs.sentinel
        if (!el) return
        const r = el.getBoundingClientRect()
        if (r.top < window.innerHeight + SENTINEL_MARGIN) {
          this.load(false)
        }
      })
    },

    /**
     * 只刷新列表裡的某一條（關詳情時用）。
     * 詳情裡畫質提升會把原影片替換掉，卡片得跟著變；整頁重載會把捲動位置沖掉 ——
     * 使用者正翻到第 5 頁，不該被彈回頂部。
     */
    async refreshOne(id) {
      if (!id) return
      const idx = this.list.findIndex((x) => x.id === id)
      if (idx < 0) return
      try {
        const d = await fetchJobDetail(id)
        if (d && d.job) {
          this.$set(this.list, idx, { ...this.list[idx], ...d.job, materials: d.materials || [] })
        }
      } catch (e) {
        // 刷新失敗不打擾使用者：卡片還是舊的，手動重新整理即可
      }
    },

    /* ---------- 產物預覽 ---------- */

    /** 本任務的產物（後端隨列表一次帶回，不用逐條請求詳情） */
    matsOf(row) {
      return (row && row.materials) || []
    },
    isImage(p) {
      return IMAGE_RE.test(p || '')
    },
    isVideo(p) {
      return VIDEO_RE.test(p || '')
    },
    /** 圖片產物（按素材順序，最多取 3 張來疊放，放大時能翻全部） */
    imagesOf(row) {
      const out = []
      this.matsOf(row).forEach((m) => {
        const p = m && m.file_path
        if (p && this.isImage(p)) {
          out.push(p)
        }
      })
      return out
    },
    /** 影片產物：有就顯示影片（影片比圖片更能說明結果），取第一條 */
    videoOf(row) {
      let found = ''
      this.matsOf(row).forEach((m) => {
        if (found) return
        const p = m && m.file_path
        if (p && this.isVideo(p)) {
          found = p
        }
      })
      return found
    },
    /**
     * 多圖疊放：越靠後的越往右下錯開一點、蓋在上面 —— 看著像一疊照片。
     * 用 transform 而不是改 left/top，元素不需要重排。
     * 步長只有 4px：預覽格不寬（140px），錯開太多會溢出到右側文字區去。
     */
    stackStyle(i) {
      if (i === 0) {
        return { zIndex: 1 }
      }
      const d = i * 4
      return {
        zIndex: i + 1,
        transform: 'translate(' + d + 'px, ' + d + 'px) scale(' + (1 - i * 0.04) + ')'
      }
    },
    playVideo(row) {
      this.videoPath = this.videoOf(row)
      this.videoTitle = '任務 #' + row.id + ' 影片'
      this.videoVisible = true
    },
    blankIcon(row) {
      if (row.status === 'failed') return 'el-icon-warning-outline'
      if (row.status === 'success') return 'el-icon-picture-outline'
      if (row.status === 'cancelled') return 'el-icon-remove-outline'
      return 'el-icon-loading'
    },
    blankText(row) {
      if (row.status === 'failed') return '生成失敗'
      if (row.status === 'success') return '暫無預覽'
      if (row.status === 'cancelled') return '已取消'
      if (row.status === 'running') return '生成中…'
      return '排隊中…'
    },

    /* ---------- 狀態與類型 ---------- */

    statusLabel(s) {
      return STATUS_LABEL[s] || s || '-'
    },
    /**
     * 卡片上的任務類型。
     * 影片特殊：創作和復刻提交的 kit_type **都是 "video"**，
     * 後端按子任務 params（ref_video / prompt_clone）判出 remake 標記，這裡據此分開。
     */
    typeOf(row) {
      const k = (row && row.kit_type) || ''
      if (k === 'video') {
        return row.remake ? '影片復刻' : '影片創作'
      }
      return KIT_LABEL[k] || k || '-'
    },
    dotOf(s) {
      return STATUS_DOT[s] || 'wait'
    },
    /** 列表裡只到分鐘就夠了，秒沒必要占位置 */
    shortTime(t) {
      return t ? String(t).slice(0, 16) : ''
    },
    canCancel(status) {
      return ['pending', 'queued', 'running', 'retrying'].indexOf(status) >= 0
    },

    /* ---------- 操作 ---------- */

    openDetail(row) {
      this.detailId = row.id
      this.detailVisible = true
    },
    async onCancel(row) {
      try {
        await this.$confirm(`確認取消任務 #${row.id} 嗎？`, '提示', { type: 'warning' })
      } catch (e) {
        return
      }
      try {
        await cancelJob(row.id)
        this.$message.success('已取消')
        this.load(true)
      } catch (e) {
        this.$message.error(e.message || '取消失敗')
      }
    }
  }
}
</script>

<style scoped>
.job-center {
  padding: 4px 2px 0;
}

.jc-bar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;           /* 窄屏時「重新整理」能換到下一行，不被篩標籤擠扁 */
  gap: 12px;
  margin-bottom: 12px;
}

.jc-filters {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.jc-filter-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.jc-filter-label {
  flex: 0 0 auto;
  margin-right: 2px;
  font-size: 12px;
  color: var(--c-text-4);
}

/* 篩選標籤（狀態 / 類型） */
.jc-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 10px;
  border: 1px solid var(--c-border-2);
  border-radius: 13px;
  background: var(--c-surface-2);
  color: var(--c-text-2);
  font-size: 12px;
  line-height: 18px;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.jc-chip:hover {
  border-color: var(--c-primary-line);
  color: var(--c-primary-ink);
}

.jc-chip.on {
  border-color: var(--c-primary);
  background: var(--c-primary-weak);
  color: var(--c-primary-ink);
  font-weight: 500;
}

.jc-refresh {
  flex: 0 0 auto;
}

/* 卡片網格：橫向卡片比較寬，所以每列至少 320px —— 寬屏三列、中屏兩列。
   捲軸留在這個容器裡（max-height 由 JS 按當前宿主量出來），捲的是列表而不是整個彈窗。 */
.jc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 12px;                 /* 間距放寬一點，卡片多時不會糊成一片 */
  align-content: start;      /* 卡片少時貼著頂部，不要被拉高鋪滿 */
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 2px;
}

/* 卡片：固定高度 —— 同一行等高、翻頁時高度也不跳。
   深色主題下陰影幾乎看不見，所以邊界主要靠 --c-border-2 的邊框（比 --c-border 明顯）。 */
.jc-card {
  display: flex;
  align-items: stretch;
  height: 96px;
  background: var(--c-surface);
  border: 1px solid var(--c-border-2);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.35), 0 2px 8px rgba(0, 0, 0, 0.25);
  transition: box-shadow 0.18s, border-color 0.18s, transform 0.18s;
}

.jc-card:hover {
  border-color: var(--c-primary-line);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.45);
  transform: translateY(-1px);
}

/* 預覽區：寬度**跟著素材比例走**（不寫死、不留黑）。
   直式成片就窄一點、橫式就寬一點。卡片整體高度固定 96px，預覽內容高度就是 96 - 2*8 = 80px。 */
.jc-preview {
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: center;
  flex: 0 0 auto;
  padding: 8px;
  background: var(--c-surface-3);
  border-right: 1px solid var(--c-border);
}

.jc-video-box {
  position: relative;
  flex: 0 0 auto;
  min-width: 44px;        /* 影片 metadata 還沒到時先佔個位（直式 9:16 約 45px），別讓卡片左邊空一拍 */
  max-width: 180px;       /* 超寬素材（21:9 之類）也不許把文字區擠沒 */
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  background: #000;
}

.jc-video-thumb {
  display: block;
  height: 100%;
  width: auto;            /* 按素材自身比例，不裁不拉也不留黑 */
  max-width: 180px;
  object-fit: contain;
}

.jc-video-play {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 22px;
  color: rgba(255, 255, 255, 0.94);
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.7);
}

/* 多圖疊放：容器留出偏移空間，圖片絕對定位錯開。
   寬度給固定值 —— 圖片產物多是方圖/4:3，按內容定寬反而會隨圖亂跳。 */
.jc-stack {
  position: relative;
  flex: 0 0 auto;
  width: 104px;
}

.jc-stack-img {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  /* 縮放從左上角起：和 translate 的偏移方向一致，層次才看得清 */
  transform-origin: left top;
  border-radius: 6px;
  /* 用表面色描邊：深色主題下才看得出「一疊照片」的層次 */
  border: 1px solid var(--c-surface);
  background: var(--c-surface-2);
  cursor: zoom-in;
  overflow: hidden;
}

/* 多圖時右下角的 "+N" 角標 */
.jc-badge {
  position: absolute;
  right: 2px;
  bottom: 2px;
  z-index: 9;
  padding: 1px 7px;
  border-radius: 9px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 11px;
  line-height: 17px;
}

.jc-blank {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 0 0 auto;
  width: 104px;
  color: var(--c-text-3);
  font-size: 11px;
  border-radius: 6px;
  background: var(--c-surface-2);
}

.jc-blank i {
  font-size: 22px;
  color: var(--c-text-4);
}

/* 右側資訊區：撐滿剩餘寬度；min-width:0 是為了讓長文字能省略而不是把卡片頂寬 */
.jc-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
  padding: 8px 10px 6px;
}

.jc-head {
  display: flex;
  align-items: center;
  gap: 6px;
  /* 不用 space-between：#id、類型、狀態三項會各散一方；狀態用 margin-left:auto 靠右更整齊 */
}

.jc-no {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: var(--c-text-1);
}

/* 任務類型（電商主圖 / 影片復刻 …）：做成小標籤，跟 #id 和狀態區分開 */
.jc-type {
  flex: 0 0 auto;
  padding: 0 5px;
  border-radius: 4px;
  background: var(--c-surface-3);
  color: var(--c-text-3);
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
}

.jc-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;         /* 靠右 */
  font-size: 11.5px;
  color: var(--c-text-2);
  white-space: nowrap;
}

.jc-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--c-text-4);
}

/* 圓點用主題色：深色主題下才不會刺眼、也跟著品牌色走 */
.jc-dot-ok {
  background: var(--c-success);
}

.jc-dot-err {
  background: var(--c-danger);
}

.jc-dot-run {
  background: var(--c-primary);
  animation: jc-pulse 1.4s ease-in-out infinite;
}

.jc-dot-wait {
  background: var(--c-text-4);
}

@keyframes jc-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}

.jc-line {
  font-size: 11.5px;
  color: var(--c-text-3);
  /* 時間這類文字可能偏長，省略而不是把卡片頂寬 */
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.jc-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  margin-top: auto;          /* 按鈕貼著卡片底部 */
  border-top: 1px solid var(--c-border);
  padding-top: 3px;
}

.jc-none {
  grid-column: 1 / -1;
  padding: 48px 0;
  text-align: center;
  color: var(--c-text-3);
  font-size: 13px;
}

/* 「載入中」提示：做得盡量輕，不搶戲（原本的「繼續下滑載入更多 / 沒有更多了」已去掉） */
.jc-loading {
  grid-column: 1 / -1;
  padding: 10px 0 2px;
  text-align: center;
  font-size: 12px;
  color: var(--c-text-4);
}

/* 下滑載入的哨兵：占 1px，看不見但能被 IntersectionObserver 觀察到 */
.jc-sentinel {
  grid-column: 1 / -1;
  height: 1px;
}

/* 窄屏（手機）：左右排列擠不開，退回「上圖下文」的堆疊（固定高度就不適用了，改回自適應） */
@media (max-width: 620px) {
  .jc-grid {
    grid-template-columns: 1fr;
  }

  .jc-card {
    flex-direction: column;
    height: auto;
  }

  .jc-preview {
    flex: none;
    width: 100%;
    height: 150px;
    border-right: none;
    border-bottom: 1px solid var(--c-border);
  }

  /* 窄屏時整行都是預覽區，素材可以放寬（桌面上的 180px 上限沒必要了） */
  .jc-video-box,
  .jc-video-thumb {
    max-width: none;
  }

  .jc-stack,
  .jc-blank {
    width: auto;
    min-width: 104px;
  }
}
</style>

<!-- 影片彈窗是 append-to-body 的，樣式不能放在 scoped 塊裡（拿不到作用域屬性） -->
<style>
.jc-video-dialog .jc-video {
  width: 100%;
  max-height: 68vh;
  background: #000;
  border-radius: 8px;
}
</style>
